import { useEffect, useId, useState } from 'react';
import { facilitySearch, type FacilitySearchRow } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { SEARCH_DEBOUNCE_MS, SEARCH_LIMIT } from '../../ui/FacilityPicker';

/**
 * The design's one-line facility filter ("All facilities · type to search", 230px),
 * searching on the server exactly like the shared FacilityPicker (GET /facilities/search,
 * debounced, 20 at a time, never the full list). It is drawn locally because the shared
 * picker carries its own label and field styles, which the design's filter row does not.
 * Clearing the field goes back to every facility.
 */
export function FacilitySearch({ value, onPick, label = 'Facility' }: { value: FacilitySearchRow | null; onPick: (row: FacilitySearchRow | null) => void; label?: string }) {
  const { client } = useSession();
  const id = useId();
  const [q, setQ] = useState(value?.name ?? '');
  const [rows, setRows] = useState<FacilitySearchRow[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const term = q.trim();
    if (!term || term === value?.name) {
      setRows([]);
      return;
    }
    const ctrl = new AbortController();
    const t = window.setTimeout(() => {
      facilitySearch(client, { query: { q: term, limit: SEARCH_LIMIT }, signal: ctrl.signal })
        .then((r) => {
          if (!ctrl.signal.aborted) {
            setRows(r.rows);
            setActive(0);
          }
        })
        .catch(() => undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      ctrl.abort();
      window.clearTimeout(t);
    };
  }, [q, client, value?.name]);

  const pick = (r: FacilitySearchRow) => {
    onPick(r);
    setQ(r.name);
    setOpen(false);
  };
  const show = open && rows.length > 0;
  return (
    <span style={sx('position:relative;display:inline-flex;')}>
      <input
        role="combobox"
        aria-label={label}
        aria-expanded={show}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        autoComplete="off"
        value={q}
        placeholder="All facilities · type to search"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          if (!e.target.value.trim()) onPick(null);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(rows.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter' && rows[active]) {
            e.preventDefault();
            pick(rows[active]);
          } else if (e.key === 'Escape') setOpen(false);
        }}
        style={sx('box-sizing:border-box;width:230px;min-height:34px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#FCFAF4;font-family:inherit;font-size:16px;font-weight:600;color:#22272E;padding:0 10px;')}
      />
      {show && (
        <ul id={`${id}-list`} role="listbox" aria-label={`${label} results`} style={sx('position:absolute;top:38px;left:0;z-index:40;min-width:260px;margin:0;padding:4px 0;list-style:none;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:10px;box-shadow:0 14px 34px rgba(34,39,46,.16);max-height:280px;overflow-y:auto;')}>
          {rows.map((r, i) => (
            <li
              key={r.id}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(r);
              }}
              onMouseEnter={() => setActive(i)}
              style={sx('padding:7px 12px;font-size:16px;cursor:pointer;color:#22272E;' + (i === active ? 'background:rgba(79,138,214,.08);' : ''))}
            >
              {r.name}
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
